/**
 * Centralized registry of all content builder elements
 * Defines element types, categories, capabilities, and default properties
 */

import React from 'react';
import {
  Box, Rows, RectangleVertical, Grid, Table, Square,
  Heading, FileText, Type, Minus, List, ListOrdered,
  Image, Images, Video, FileType, ChevronsUpDown, CheckSquare,
  CircleDot, PenTool, MousePointerClick, Target, Users, BookOpen,
  LayoutList
} from 'lucide-react';

// Element categories for organization
export const ELEMENT_CATEGORIES = {
  LAYOUT: 'layout',
  CONTENT: 'content',
  MEDIA: 'media',
  FORM: 'form',
  ACTION: 'action',
  DYNAMIC: 'dynamic'
};

// Define what each element type can contain (for nesting validation)
export const CONTAINER_CAPABILITIES = {
  section: ['row', 'grid', 'grid-container', 'element'],
  row: ['column'],
  column: ['row', 'grid', 'grid-container', 'element'],
  grid: ['element'],
  'grid-container': ['grid-cell'],
  'grid-cell': ['element', 'row', 'grid'],
  // Non-containers can't contain anything
  element: []
};

/**
 * LAYOUT ELEMENTS
 * Containers that define page structure and positioning
 */
export const LAYOUT_ELEMENTS = [
  {
    type: 'section',
    label: 'Section',
    icon: <Box size={20} />,
    category: ELEMENT_CATEGORIES.LAYOUT,
    description: 'Full-width container with background',
    isContainer: true,
    canContain: CONTAINER_CAPABILITIES.section,
    defaultProps: {
      background: 'transparent',
      backgroundColor: '#ffffff',
      padding: 'medium',
      maxWidth: 'container', // 'full' | 'container' | 'narrow'
      marginTop: 'none',
      marginBottom: 'none'
    },
    schema: {
      background: { type: 'select', options: ['transparent', 'color', 'gradient'] },
      backgroundColor: { type: 'color' },
      padding: { type: 'select', options: ['none', 'small', 'medium', 'large', 'xlarge'] },
      maxWidth: { type: 'select', options: ['full', 'container', 'narrow'] },
      marginTop: { type: 'select', options: ['none', 'small', 'medium', 'large'] },
      marginBottom: { type: 'select', options: ['none', 'small', 'medium', 'large'] }
    }
  },

  {
    type: 'row',
    label: 'Row / Columns',
    icon: <Rows size={20} />,
    category: ELEMENT_CATEGORIES.LAYOUT,
    description: 'Horizontal 12-column grid layout',
    isContainer: true,
    canContain: CONTAINER_CAPABILITIES.row,
    defaultProps: {
      gap: 'medium',
      alignment: 'stretch', // 'top' | 'center' | 'bottom' | 'stretch'
      reverseOnMobile: false
    },
    schema: {
      gap: { type: 'select', options: ['none', 'small', 'medium', 'large', 'xlarge'] },
      alignment: { type: 'select', options: ['top', 'center', 'bottom', 'stretch'] },
      reverseOnMobile: { type: 'boolean' }
    },
    // Predefined column presets (using 12-column spans)
    presets: [
      { label: '1 Column', columns: [{ span: 12 }] },
      { label: '2 Equal (6+6)', columns: [{ span: 6 }, { span: 6 }] },
      { label: '2 Columns (8+4)', columns: [{ span: 8 }, { span: 4 }] },
      { label: '2 Columns (4+8)', columns: [{ span: 4 }, { span: 8 }] },
      { label: '2 Columns (9+3)', columns: [{ span: 9 }, { span: 3 }] },
      { label: '3 Equal (4+4+4)', columns: [{ span: 4 }, { span: 4 }, { span: 4 }] },
      { label: '3 Columns (3+6+3)', columns: [{ span: 3 }, { span: 6 }, { span: 3 }] },
      { label: '4 Equal (3+3+3+3)', columns: [{ span: 3 }, { span: 3 }, { span: 3 }, { span: 3 }] },
      { label: '6 Equal (2+2+2+2+2+2)', columns: [{ span: 2 }, { span: 2 }, { span: 2 }, { span: 2 }, { span: 2 }, { span: 2 }] }
    ]
  },

  {
    type: 'column',
    label: 'Column',
    icon: <RectangleVertical size={20} />,
    category: ELEMENT_CATEGORIES.LAYOUT,
    description: 'Vertical container within row (1-12 columns)',
    isContainer: true,
    canContain: CONTAINER_CAPABILITIES.column,
    defaultProps: {
      span: 12, // 1-12 columns to span
      padding: 'medium',
      backgroundColor: 'transparent',
      // Flexbox layout controls
      flexDirection: 'column', // 'row' | 'column' | 'row-reverse' | 'column-reverse'
      justifyContent: 'flex-start', // 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly'
      alignItems: 'stretch', // 'flex-start' | 'center' | 'flex-end' | 'stretch' | 'baseline'
      gap: 'medium' // 'none' | 'small' | 'medium' | 'large'
    },
    schema: {
      span: { type: 'number', min: 1, max: 12, label: 'Column Span (1-12)' },
      padding: { type: 'select', options: ['none', 'small', 'medium', 'large'], label: 'Padding' },
      backgroundColor: { type: 'color', label: 'Background Color' },
      flexDirection: { type: 'select', options: ['column', 'row', 'column-reverse', 'row-reverse'], label: 'Flex Direction' },
      justifyContent: { type: 'select', options: ['flex-start', 'center', 'flex-end', 'space-between', 'space-around', 'space-evenly'], label: 'Justify Content (main axis)' },
      alignItems: { type: 'select', options: ['stretch', 'flex-start', 'center', 'flex-end', 'baseline'], label: 'Align Items (cross axis)' },
      gap: { type: 'select', options: ['none', 'small', 'medium', 'large'], label: 'Gap Between Items' }
    }
  },

  {
    type: 'grid',
    label: 'Grid',
    icon: <Grid size={20} />,
    category: ELEMENT_CATEGORIES.LAYOUT,
    description: 'Auto-arranging grid layout',
    isContainer: true,
    canContain: CONTAINER_CAPABILITIES.grid,
    defaultProps: {
      columns: {
        mobile: 1,
        tablet: 2,
        desktop: 3
      },
      gap: 'medium',
      itemMinWidth: '250px'
    },
    schema: {
      gap: { type: 'select', options: ['none', 'small', 'medium', 'large'] },
      itemMinWidth: { type: 'text' }
    }
  },

  {
    type: 'grid-container',
    label: 'Visual Grid',
    icon: <Table size={20} />,
    category: ELEMENT_CATEGORIES.LAYOUT,
    description: 'Visual grid with rows & columns (like table)',
    isContainer: true,
    canContain: ['grid-cell'],
    acceptsDropFrom: [], // Grid container itself doesn't accept drops - only cells do
    defaultProps: {
      rows: 2,
      columns: 1,
      gap: 'medium',
      minRowHeight: '100px',
      backgroundColor: 'transparent'
    },
    schema: {
      rows: { type: 'number', min: 1, max: 12, label: 'Number of Rows' },
      columns: { type: 'number', min: 1, max: 12, label: 'Number of Columns' },
      gap: { type: 'select', options: ['none', 'small', 'medium', 'large'] },
      minRowHeight: { type: 'text', label: 'Min Row Height (e.g., 100px, 10rem)' },
      backgroundColor: { type: 'color' }
    }
  },

  {
    type: 'grid-cell',
    label: 'Grid Cell',
    icon: <Square size={20} />,
    category: ELEMENT_CATEGORIES.LAYOUT,
    description: 'Individual cell in visual grid',
    isContainer: true,
    canContain: ['element'],
    acceptsDropFrom: [ELEMENT_CATEGORIES.CONTENT, ELEMENT_CATEGORIES.MEDIA, ELEMENT_CATEGORIES.FORM, ELEMENT_CATEGORIES.ACTION], // Only accepts content/form elements
    defaultProps: {
      rowSpan: 1,
      columnSpan: 1,
      rowStart: null,
      columnStart: null,
      padding: 'medium',
      backgroundColor: 'transparent',
      // Flexbox layout controls
      flexDirection: 'column', // 'row' | 'column' | 'row-reverse' | 'column-reverse'
      justifyContent: 'flex-start', // 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly'
      alignItems: 'stretch', // 'flex-start' | 'center' | 'flex-end' | 'stretch' | 'baseline'
      gap: 'small' // 'none' | 'small' | 'medium' | 'large'
    },
    schema: {
      rowSpan: { type: 'number', min: 1, max: 12, label: 'Row Span (merge rows)' },
      columnSpan: { type: 'number', min: 1, max: 12, label: 'Column Span (merge columns)' },
      rowStart: { type: 'number', min: 1, max: 12, label: 'Row Start Position (optional)' },
      columnStart: { type: 'number', min: 1, max: 12, label: 'Column Start Position (optional)' },
      padding: { type: 'select', options: ['none', 'small', 'medium', 'large'], label: 'Padding' },
      backgroundColor: { type: 'color', label: 'Background Color' },
      flexDirection: { type: 'select', options: ['column', 'row', 'column-reverse', 'row-reverse'], label: 'Flex Direction' },
      justifyContent: { type: 'select', options: ['flex-start', 'center', 'flex-end', 'space-between', 'space-around', 'space-evenly'], label: 'Justify Content (main axis)' },
      alignItems: { type: 'select', options: ['stretch', 'flex-start', 'center', 'flex-end', 'baseline'], label: 'Align Items (cross axis)' },
      gap: { type: 'select', options: ['none', 'small', 'medium', 'large'], label: 'Gap Between Items' }
    }
  }
];

/**
 * CONTENT ELEMENTS
 * Text and content blocks
 */
export const CONTENT_ELEMENTS = [
  {
    type: 'heading',
    label: 'Heading',
    icon: <Heading size={20} />,
    category: ELEMENT_CATEGORIES.CONTENT,
    description: 'Heading text (H1-H6)',
    isContainer: false,
    defaultContent: {
      text: 'Heading Text',
      level: 'h2', // 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
      alignment: 'left' // 'left' | 'center' | 'right'
    },
    schema: {
      text: { type: 'text', required: true },
      level: { type: 'select', options: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] },
      alignment: { type: 'select', options: ['left', 'center', 'right'] }
    }
  },

  {
    type: 'paragraph',
    label: 'Paragraph',
    icon: <FileText size={20} />,
    category: ELEMENT_CATEGORIES.CONTENT,
    description: 'Simple paragraph text',
    isContainer: false,
    defaultContent: {
      text: 'Click to edit this paragraph text. You can enter any content here.',
      alignment: 'left',
      fontSize: 'normal' // 'small' | 'normal' | 'large'
    },
    schema: {
      text: { type: 'textarea', required: true },
      alignment: { type: 'select', options: ['left', 'center', 'right', 'justify'] },
      fontSize: { type: 'select', options: ['small', 'normal', 'large'] }
    }
  },

  {
    type: 'richtext',
    label: 'Rich Text',
    icon: <Type size={20} />,
    category: ELEMENT_CATEGORIES.CONTENT,
    description: 'Formatted text with styling',
    isContainer: false,
    defaultContent: {
      html: '<p>Click to edit this rich text block. Perfect for detailed content with <strong>bold</strong>, <em>italic</em>, and <u>underline</u> formatting.</p>',
      alignment: 'left',
      fontSize: 'normal'
    },
    schema: {
      html: { type: 'richtext', required: true },
      alignment: { type: 'select', options: ['left', 'center', 'right', 'justify'] },
      fontSize: { type: 'select', options: ['small', 'normal', 'large'] }
    }
  },

  {
    type: 'divider',
    label: 'Divider',
    icon: <Minus size={20} />,
    category: ELEMENT_CATEGORIES.CONTENT,
    description: 'Horizontal line separator',
    isContainer: false,
    defaultContent: {
      style: 'solid', // 'solid' | 'dashed' | 'dotted'
      width: '100%',
      color: '#cccccc',
      spacing: 'medium' // 'small' | 'medium' | 'large'
    },
    schema: {
      style: { type: 'select', options: ['solid', 'dashed', 'dotted'] },
      width: { type: 'text' },
      color: { type: 'color' },
      spacing: { type: 'select', options: ['small', 'medium', 'large'] }
    }
  },

  {
    type: 'bullet-list',
    label: 'Bullet List',
    icon: <List size={20} />,
    category: ELEMENT_CATEGORIES.CONTENT,
    description: 'Unordered list with bullet points',
    isContainer: false,
    defaultContent: {
      items: ['List item 1', 'List item 2', 'List item 3'],
      bulletStyle: 'disc', // 'disc' | 'circle' | 'square'
      alignment: 'left', // 'left' | 'center' | 'right'
      fontSize: 'normal' // 'small' | 'normal' | 'large'
    },
    schema: {
      items: { type: 'array', required: true, label: 'List Items' },
      bulletStyle: { type: 'select', options: ['disc', 'circle', 'square'], label: 'Bullet Style' },
      alignment: { type: 'select', options: ['left', 'center', 'right'] },
      fontSize: { type: 'select', options: ['small', 'normal', 'large'] }
    }
  },

  {
    type: 'ordered-list',
    label: 'Numbered List',
    icon: <ListOrdered size={20} />,
    category: ELEMENT_CATEGORIES.CONTENT,
    description: 'Ordered list with numbers or letters',
    isContainer: false,
    defaultContent: {
      items: ['First item', 'Second item', 'Third item'],
      numberStyle: 'decimal', // 'decimal' | 'lower-alpha' | 'upper-alpha' | 'lower-roman' | 'upper-roman'
      alignment: 'left', // 'left' | 'center' | 'right'
      fontSize: 'normal', // 'small' | 'normal' | 'large'
      startNumber: 1 // Starting number for the list
    },
    schema: {
      items: { type: 'array', required: true, label: 'List Items' },
      numberStyle: {
        type: 'select',
        options: ['decimal', 'lower-alpha', 'upper-alpha', 'lower-roman', 'upper-roman'],
        label: 'Numbering Style',
        descriptions: {
          'decimal': '1, 2, 3',
          'lower-alpha': 'a, b, c',
          'upper-alpha': 'A, B, C',
          'lower-roman': 'i, ii, iii',
          'upper-roman': 'I, II, III'
        }
      },
      alignment: { type: 'select', options: ['left', 'center', 'right'] },
      fontSize: { type: 'select', options: ['small', 'normal', 'large'] },
      startNumber: { type: 'number', min: 1, label: 'Start Number' }
    }
  }
];

/**
 * MEDIA ELEMENTS
 * Images, videos, galleries
 */
export const MEDIA_ELEMENTS = [
  {
    type: 'image',
    label: 'Image',
    icon: <Image size={20} />,
    category: ELEMENT_CATEGORIES.MEDIA,
    description: 'Single image with caption',
    isContainer: false,
    defaultContent: {
      src: '',
      alt: '',
      caption: '',
      alignment: 'center', // 'left' | 'center' | 'right'
      size: 'medium', // 'small' | 'medium' | 'large' | 'full'
      link: '',
      borderRadius: 'none' // 'none' | 'small' | 'medium' | 'large' | 'circle'
    },
    schema: {
      src: { type: 'image-upload', required: true },
      alt: { type: 'text', required: true },
      caption: { type: 'text' },
      alignment: { type: 'select', options: ['left', 'center', 'right'] },
      size: { type: 'select', options: ['small', 'medium', 'large', 'full'] },
      link: { type: 'text' },
      borderRadius: { type: 'select', options: ['none', 'small', 'medium', 'large', 'circle'] }
    }
  },

  {
    type: 'gallery',
    label: 'Image Gallery',
    icon: <Images size={20} />,
    category: ELEMENT_CATEGORIES.MEDIA,
    description: 'Grid of images',
    isContainer: false,
    defaultContent: {
      images: [],
      layout: 'grid-3', // 'grid-2' | 'grid-3' | 'grid-4' | 'carousel'
      gap: 'medium',
      lightbox: true
    },
    schema: {
      images: { type: 'image-array' },
      layout: { type: 'select', options: ['grid-2', 'grid-3', 'grid-4', 'carousel'] },
      gap: { type: 'select', options: ['small', 'medium', 'large'] },
      lightbox: { type: 'boolean' }
    }
  },

  {
    type: 'video',
    label: 'Video',
    icon: <Video size={20} />,
    category: ELEMENT_CATEGORIES.MEDIA,
    description: 'Embedded video (YouTube, Vimeo)',
    isContainer: false,
    defaultContent: {
      url: '',
      provider: 'youtube', // 'youtube' | 'vimeo' | 'custom'
      autoplay: false,
      controls: true,
      aspectRatio: '16:9' // '16:9' | '4:3' | '1:1'
    },
    schema: {
      url: { type: 'text', required: true },
      provider: { type: 'select', options: ['youtube', 'vimeo', 'custom'] },
      autoplay: { type: 'boolean' },
      controls: { type: 'boolean' },
      aspectRatio: { type: 'select', options: ['16:9', '4:3', '1:1'] }
    }
  }
];

/**
 * FORM ELEMENTS
 * Input fields for data collection
 */
export const FORM_ELEMENTS = [
  {
    type: 'text-input',
    label: 'Text Input',
    icon: <FileType size={20} />,
    category: ELEMENT_CATEGORIES.FORM,
    description: 'Single-line text field',
    isContainer: false,
    defaultContent: {
      label: 'Text Field',
      name: '',
      placeholder: 'Enter text...',
      required: false,
      helpText: ''
    },
    schema: {
      label: { type: 'text', required: true },
      name: { type: 'text', required: true },
      placeholder: { type: 'text' },
      required: { type: 'boolean' },
      helpText: { type: 'text' }
    }
  },

  {
    type: 'textarea',
    label: 'Text Area',
    icon: <FileText size={20} />,
    category: ELEMENT_CATEGORIES.FORM,
    description: 'Multi-line text field',
    isContainer: false,
    defaultContent: {
      label: 'Text Area',
      name: '',
      placeholder: 'Enter multiple lines...',
      required: false,
      rows: 4
    },
    schema: {
      label: { type: 'text', required: true },
      name: { type: 'text', required: true },
      placeholder: { type: 'text' },
      required: { type: 'boolean' },
      rows: { type: 'number', min: 2, max: 10 }
    }
  },

  {
    type: 'select',
    label: 'Dropdown',
    icon: <ChevronsUpDown size={20} />,
    category: ELEMENT_CATEGORIES.FORM,
    description: 'Select dropdown menu',
    isContainer: false,
    defaultContent: {
      label: 'Dropdown',
      name: '',
      options: ['Option 1', 'Option 2', 'Option 3'],
      required: false,
      placeholder: 'Choose an option...'
    },
    schema: {
      label: { type: 'text', required: true },
      name: { type: 'text', required: true },
      options: { type: 'array', required: true },
      required: { type: 'boolean' },
      placeholder: { type: 'text' }
    }
  },

  {
    type: 'checkbox',
    label: 'Checkbox',
    icon: <CheckSquare size={20} />,
    category: ELEMENT_CATEGORIES.FORM,
    description: 'Single checkbox',
    isContainer: false,
    defaultContent: {
      label: 'Checkbox',
      name: '',
      text: 'I agree to the terms',
      required: false
    },
    schema: {
      label: { type: 'text' },
      name: { type: 'text', required: true },
      text: { type: 'text', required: true },
      required: { type: 'boolean' }
    }
  },

  {
    type: 'radio',
    label: 'Radio Group',
    icon: <CircleDot size={20} />,
    category: ELEMENT_CATEGORIES.FORM,
    description: 'Radio button group',
    isContainer: false,
    defaultContent: {
      label: 'Radio Group',
      name: '',
      options: ['Option 1', 'Option 2'],
      required: false
    },
    schema: {
      label: { type: 'text', required: true },
      name: { type: 'text', required: true },
      options: { type: 'array', required: true },
      required: { type: 'boolean' }
    }
  },

  {
    type: 'signature',
    label: 'Digital Signature',
    icon: <PenTool size={20} />,
    category: ELEMENT_CATEGORIES.FORM,
    description: 'Digital signature pad',
    isContainer: false,
    defaultContent: {
      label: 'Digital Signature',
      name: 'signature',
      required: true,
      instructionText: 'By providing your digital signature below you agree to the terms and conditions of this document',
      legalNoticeText: 'Your digital signature will be timestamped and legally binding. This document will be securely stored and available for download.'
    },
    schema: {
      label: { type: 'text', required: true },
      name: { type: 'text', required: true },
      required: { type: 'boolean' },
      instructionText: { type: 'textarea' },
      legalNoticeText: { type: 'textarea' }
    }
  }
];

/**
 * ACTION ELEMENTS
 * Buttons, CTAs, links
 */
export const ACTION_ELEMENTS = [
  {
    type: 'button',
    label: 'Button',
    icon: <MousePointerClick size={20} />,
    category: ELEMENT_CATEGORIES.ACTION,
    description: 'Clickable button',
    isContainer: false,
    defaultContent: {
      text: 'Click Here',
      link: '',
      variant: 'primary', // 'primary' | 'secondary' | 'outline'
      size: 'medium', // 'small' | 'medium' | 'large'
      alignment: 'left', // 'left' | 'center' | 'right'
      openInNewTab: false
    },
    schema: {
      text: { type: 'text', required: true },
      link: { type: 'text', required: true },
      variant: { type: 'select', options: ['primary', 'secondary', 'outline'] },
      size: { type: 'select', options: ['small', 'medium', 'large'] },
      alignment: { type: 'select', options: ['left', 'center', 'right'] },
      openInNewTab: { type: 'boolean' }
    }
  },

  {
    type: 'cta',
    label: 'Call to Action',
    icon: <Target size={20} />,
    category: ELEMENT_CATEGORIES.ACTION,
    description: 'Full CTA block with heading and button',
    isContainer: false,
    defaultContent: {
      heading: 'Ready to Get Started?',
      description: 'Join our club today and be part of something amazing.',
      buttonText: 'Sign Up Now',
      buttonLink: '/register',
      backgroundColor: '#27AE60',
      textColor: '#ffffff',
      alignment: 'center'
    },
    schema: {
      heading: { type: 'text', required: true },
      description: { type: 'textarea' },
      buttonText: { type: 'text', required: true },
      buttonLink: { type: 'text', required: true },
      backgroundColor: { type: 'color' },
      textColor: { type: 'color' },
      alignment: { type: 'select', options: ['left', 'center', 'right'] }
    }
  }
];

/**
 * DYNAMIC ELEMENTS
 * Elements that pull data from the system
 */
export const DYNAMIC_ELEMENTS = [
  {
    type: 'staff-directory',
    label: 'Staff Directory',
    icon: <Users size={20} />,
    category: ELEMENT_CATEGORIES.DYNAMIC,
    description: 'Display club coaches/staff',
    isContainer: false,
    defaultContent: {
      title: 'Meet Our Staff',
      layout: 'grid', // 'grid' | 'list'
      showBio: true,
      showPhoto: true,
      showContact: false,
      filterByRole: 'all' // 'all' | 'coach' | 'admin'
    },
    schema: {
      title: { type: 'text' },
      layout: { type: 'select', options: ['grid', 'list'] },
      showBio: { type: 'boolean' },
      showPhoto: { type: 'boolean' },
      showContact: { type: 'boolean' },
      filterByRole: { type: 'select', options: ['all', 'coach', 'admin'] }
    }
  },

  {
    type: 'program-showcase',
    label: 'Program Showcase',
    icon: <BookOpen size={20} />,
    category: ELEMENT_CATEGORIES.DYNAMIC,
    description: 'Display club programs',
    isContainer: false,
    defaultContent: {
      title: 'Our Programs',
      layout: 'grid', // 'grid' | 'carousel' | 'list'
      showDescription: true,
      showPricing: false,
      filterByAgeGroup: 'all'
    },
    schema: {
      title: { type: 'text' },
      layout: { type: 'select', options: ['grid', 'carousel', 'list'] },
      showDescription: { type: 'boolean' },
      showPricing: { type: 'boolean' },
      filterByAgeGroup: { type: 'text' }
    }
  }
];

/**
 * Combine all elements into a single registry
 */
export const ALL_ELEMENTS = [
  ...LAYOUT_ELEMENTS,
  ...CONTENT_ELEMENTS,
  ...MEDIA_ELEMENTS,
  ...FORM_ELEMENTS,
  ...ACTION_ELEMENTS,
  ...DYNAMIC_ELEMENTS
];

/**
 * Get element definition by type
 */
export const getElementDefinition = (type) => {
  return ALL_ELEMENTS.find(el => el.type === type);
};

/**
 * Get elements by category
 */
export const getElementsByCategory = (category) => {
  return ALL_ELEMENTS.filter(el => el.category === category);
};

/**
 * Get elements suitable for a specific mode
 */
export const getElementsForMode = (mode) => {
  if (mode === 'form') {
    // Form mode: layout + content + form elements
    return [
      ...LAYOUT_ELEMENTS,
      ...CONTENT_ELEMENTS,
      ...FORM_ELEMENTS
    ];
  } else if (mode === 'page') {
    // Page mode: layout + content + media + action + dynamic
    return [
      ...LAYOUT_ELEMENTS,
      ...CONTENT_ELEMENTS,
      ...MEDIA_ELEMENTS,
      ...ACTION_ELEMENTS,
      ...DYNAMIC_ELEMENTS
    ];
  }

  return ALL_ELEMENTS;
};

/**
 * Validate if an element can be dropped into a container
 */
export const canDropInto = (containerType, elementType) => {
  const containerDef = getElementDefinition(containerType);

  if (!containerDef || !containerDef.isContainer) {
    return false;
  }

  const canContain = containerDef.canContain;

  // Check if element type is allowed
  if (canContain.includes(elementType)) {
    return true;
  }

  // Check if 'element' is allowed (generic content elements)
  const elementDef = getElementDefinition(elementType);
  if (canContain.includes('element') && elementDef && !elementDef.isContainer) {
    return true;
  }

  return false;
};
